// frontend/src/components/common/BankTransferInfo.js
// Shows the company's active bank accounts + support contacts, and lets a
// customer file an "I have paid" claim after a transfer. The claim is not
// proof - an admin confirms it before the wallet is credited.
import React, { useEffect, useState } from 'react';
import { settingsApi } from '../../api/settings';
import { walletApi } from '../../api/wallet';
import {
  FaUniversity, FaPhoneAlt, FaEnvelope, FaCopy, FaCheck, FaSpinner, FaMoneyCheckAlt, FaTimes
} from 'react-icons/fa';

function BankTransferInfo({ compact = false }) {
  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState('');

  // "I have paid" claim
  const [showClaim, setShowClaim] = useState(false);
  const [claimAmount, setClaimAmount] = useState('');
  const [claimBank, setClaimBank] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [claimError, setClaimError] = useState('');
  const [claimDone, setClaimDone] = useState('');

  useEffect(() => {
    (async () => {
      try {
        const data = await settingsApi.getPaymentInfo();
        setInfo(data || {});
      } catch (err) {
        setInfo({ bank_accounts: [] });
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const copy = (text, key) => {
    try {
      navigator.clipboard.writeText(text);
      setCopied(key);
      setTimeout(() => setCopied(''), 1500);
    } catch (e) { /* clipboard may be blocked; ignore */ }
  };

  const submitClaim = async () => {
    setClaimError('');
    const amt = parseFloat(claimAmount);
    if (isNaN(amt) || amt <= 0) { setClaimError('Enter the amount you paid.'); return; }
    if (!claimBank) { setClaimError('Choose which account you paid into.'); return; }
    setSubmitting(true);
    try {
      const res = await walletApi.submitTopupClaim({ amount: amt, bank_label: claimBank });
      setClaimDone(res?.message || 'Thank you. Our team will confirm your payment and credit your wallet.');
      setShowClaim(false);
      setClaimAmount('');
      setClaimBank('');
    } catch (err) {
      setClaimError(err?.message || 'Could not send your notice. Please try again or call support.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="text-sm text-gray-500 flex items-center gap-2 py-3">
        <FaSpinner className="animate-spin" /> Loading payment options...
      </div>
    );
  }

  const banks = (info && info.bank_accounts) || [];
  const phone = info?.support_phone;
  const email = info?.support_email;

  if (banks.length === 0 && !phone && !email) {
    return (
      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 text-sm text-yellow-800">
        Bank transfer details are not set up yet. Please contact support to top up your wallet.
      </div>
    );
  }

  const bankLabel = (b) => `${b.bank_name} — ${b.account_number}`;

  return (
    <div className={`bg-blue-50 border border-blue-200 rounded-lg ${compact ? 'p-4' : 'p-5'}`}>
      <h4 className="font-bold text-blue-900 flex items-center gap-2 mb-1">
        <FaUniversity /> Pay by bank transfer
      </h4>
      <p className="text-sm text-blue-800 mb-4">
        Transfer to any account below. Then tap <strong>I have paid</strong> or contact support.
        We confirm the payment and add the money to your wallet, then you can complete your payment.
      </p>

      {claimDone && (
        <div className="bg-green-50 border border-green-200 text-green-800 rounded p-3 mb-4 text-sm font-semibold flex items-start gap-2">
          <FaCheck className="mt-0.5 flex-shrink-0" /> {claimDone}
        </div>
      )}

      {banks.length > 0 && (
        <div className="space-y-2 mb-4">
          {banks.map((b, i) => (
            <div key={i} className="bg-white rounded border border-blue-100 p-3 flex items-center justify-between gap-3">
              <div className="min-w-0">
                <p className="font-bold text-gray-800 text-lg tracking-wide break-all">{b.account_number}</p>
                <p className="text-sm font-semibold text-gray-700 break-words">{b.bank_name}</p>
                {b.account_name && (
                  <p className="text-sm text-gray-600 break-words">{b.account_name}</p>
                )}
              </div>
              <button
                type="button"
                onClick={() => copy(b.account_number, `acct-${i}`)}
                className="flex-shrink-0 inline-flex items-center gap-1.5 text-sm font-semibold text-blue-600 hover:text-blue-800 border border-blue-200 rounded px-3 py-1.5"
              >
                {copied === `acct-${i}` ? <><FaCheck /> Copied</> : <><FaCopy /> Copy</>}
              </button>
            </div>
          ))}
        </div>
      )}

      {/* "I have paid" */}
      {banks.length > 0 && !showClaim && (
        <button
          type="button"
          onClick={() => { setShowClaim(true); setClaimDone(''); }}
          className="w-full inline-flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-bold px-4 py-2.5 rounded mb-3"
        >
          <FaMoneyCheckAlt /> I have paid
        </button>
      )}

      {showClaim && (
        <div className="bg-white border border-blue-200 rounded-lg p-4 mb-4">
          <div className="flex items-center justify-between mb-2">
            <p className="font-bold text-gray-800">Tell us about your payment</p>
            <button type="button" onClick={() => setShowClaim(false)} className="text-gray-400 hover:text-gray-600">
              <FaTimes />
            </button>
          </div>
          <div className="space-y-3">
            <div>
              <label className="block text-xs font-bold text-gray-700 mb-1">Amount you paid (NGN)</label>
              <input
                type="number"
                min="1"
                value={claimAmount}
                onChange={(e) => { setClaimAmount(e.target.value); setClaimError(''); }}
                placeholder="e.g. 34999"
                className="w-full p-2 border rounded text-sm"
              />
            </div>
            <div>
              <label className="block text-xs font-bold text-gray-700 mb-1">Which account did you pay into?</label>
              <select
                value={claimBank}
                onChange={(e) => { setClaimBank(e.target.value); setClaimError(''); }}
                className="w-full p-2 border rounded text-sm bg-white"
              >
                <option value="">Select the account</option>
                {banks.map((b, i) => <option key={i} value={bankLabel(b)}>{bankLabel(b)}</option>)}
              </select>
            </div>
            {claimError && <p className="text-sm text-red-600 font-semibold">{claimError}</p>}
            <button
              type="button"
              onClick={submitClaim}
              disabled={submitting}
              className="w-full inline-flex items-center justify-center gap-2 bg-green-600 hover:bg-green-700 disabled:opacity-60 text-white text-sm font-bold px-4 py-2.5 rounded"
            >
              {submitting ? <><FaSpinner className="animate-spin" /> Sending...</> : <><FaCheck /> Notify support I have paid</>}
            </button>
            <p className="text-[11px] text-gray-500 text-center">
              Your wallet is credited only after we confirm the money has arrived.
            </p>
          </div>
        </div>
      )}

      <div className="border-t border-blue-200 pt-3">
        <p className="text-sm font-semibold text-blue-900 mb-2">Or contact support:</p>
        <div className="flex flex-col sm:flex-row gap-2">
          {phone && (
            <a
              href={`tel:${phone}`}
              className="flex-1 inline-flex items-center justify-center gap-2 bg-green-600 hover:bg-green-700 text-white text-sm font-bold px-4 py-2.5 rounded"
            >
              <FaPhoneAlt /> Call {phone}
            </a>
          )}
          {email && (
            <a
              href={`mailto:${email}?subject=Wallet%20top-up%20after%20bank%20transfer`}
              className="flex-1 inline-flex items-center justify-center gap-2 border border-blue-300 text-blue-700 hover:bg-blue-100 text-sm font-bold px-4 py-2.5 rounded"
            >
              <FaEnvelope /> Email support
            </a>
          )}
        </div>
      </div>
    </div>
  );
}

export default BankTransferInfo;
